import React, { useEffect, useState } from 'react'
import { IDriverSearchObject } from 'src/types'
import DriverWorkingHours from './DriverWorkingHours'
import EditDriverBottomBar from './EditDriverBottomBar'
import { IDriverSchedule, IDriverScheduleResponse } from './DriverViewSchedule'
import { getDriverSchedule, updateDriverSchedule } from 'src/state/actions/drivers'
import history from 'src/history'
import { NotificationProvider } from '../ui/notification-provider'
import FloatingForm from '../ui/form/FloatingForm'
import { MoonLoader } from 'react-spinners'

export interface IDriverEditScheduleProps {
  operatorId: string
  orgId: string
  currentOperator: IDriverSearchObject
  sendPushNotificationToDriver: (operatorId: string, message: string) => void
}

export interface IDriverScheduleDTO {
  operatorId: string
  serviceDays: IDriverSchedule[]
}

export interface IOverlappingSchedulesResponse extends IDriverSchedule {
  schedules: IDriverSchedule[]
}

const DriverEditSchedule = (props: IDriverEditScheduleProps) => {
  const { operatorId, sendPushNotificationToDriver } = props
  const notification: any = React.useContext(NotificationProvider)
  const [isSaving, setIsSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [prevSchedule, setPrevSchedule] = useState<IDriverSchedule[]>([])
  const [workingHoursList, setWorkingHoursList] = useState<IDriverSchedule[]>([])
  const [workingHoursListErrors, setWorkingHoursListErrors] = useState<IDriverSchedule[]>([])

  useEffect(() => {
    fetchDriverSchedule(operatorId)
  }, [])

  useEffect(() => {
    const workingHourListWithoutErrorDays = workingHoursList.map(item => {
      return {
        days: item.days,
        from: item.from,
        until: item.until,
        is_archived: false
      }
    })
    const overlaps = findOverlaps(workingHourListWithoutErrorDays)
    if (overlaps.length > 0) {
      const mergedWorkingHourListErrors = mergeErrorDaysFromOverlaps(overlaps)
      setWorkingHoursListErrors(mergedWorkingHourListErrors)
    } else {
      setWorkingHoursListErrors([])
    }
  }, [workingHoursList])

  const fetchDriverSchedule = async (operatorId: string) => {
    const response: IDriverScheduleResponse = await getDriverSchedule(operatorId)
    setWorkingHoursList(response.serviceDays)
    setPrevSchedule(response.serviceDays)
    setLoading(false)
  }

  const checkForInvalidData = () => {
    const invalidData = workingHoursList.find(workingHour => {
      return workingHour.from === '' || workingHour.until === '' || workingHour.days.length === 0
    })

    if (invalidData !== undefined) {
      return true
    } else {
      return false
    }
  }

  const findOverlaps = (schedules: IDriverSchedule[]): IOverlappingSchedulesResponse[] => {
    const overlaps: IOverlappingSchedulesResponse[] = []

    for (let i = 0; i < schedules.length; i++) {
      const scheduleA = schedules[i]

      for (let j = i + 1; j < schedules.length; j++) {
        const scheduleB = schedules[j]

        const commonDays = scheduleA.days.filter(day => scheduleB.days.includes(day))

        if (commonDays.length > 0) {
          const fromA = timeToMinutes(scheduleA.from)
          const untilA = timeToMinutes(scheduleA.until)
          const fromB = timeToMinutes(scheduleB.from)
          const untilB = timeToMinutes(scheduleB.until)

          const overlapFrom = Math.max(fromA, fromB)
          const overlapUntil = Math.min(untilA, untilB)

          if (overlapFrom < overlapUntil) {
            if (!scheduleA.errorDays) {
              scheduleA.errorDays = []
            }
            if (!scheduleB.errorDays) {
              scheduleB.errorDays = []
            }

            scheduleA.errorDays.push(
              ...commonDays.filter(day => !(scheduleA.errorDays !== undefined ? scheduleA.errorDays : []).includes(day))
            )
            scheduleB.errorDays.push(
              ...commonDays.filter(day => !(scheduleB.errorDays !== undefined ? scheduleB.errorDays : []).includes(day))
            )

            const existingOverlap = overlaps.find(
              overlap =>
                overlap.from === minutesToTime(overlapFrom) &&
                overlap.until === minutesToTime(overlapUntil) &&
                commonDays.every(day => overlap.days.includes(day))
            )

            if (existingOverlap) {
              existingOverlap.schedules.push(scheduleA, scheduleB)
            } else {
              const overlapSchedule: IOverlappingSchedulesResponse = {
                days: commonDays,
                from: minutesToTime(overlapFrom),
                until: minutesToTime(overlapUntil),
                is_archived: false,
                schedules: [scheduleA, scheduleB],
                errorDays: []
              }

              overlaps.push(overlapSchedule)
            }
          }
        }
      }
    }

    overlaps.forEach(overlap => {
      overlap.schedules = Array.from(new Set(overlap.schedules))
    })

    return overlaps
  }

  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
      .toString()
      .padStart(2, '0')
    const mins = (minutes % 60).toString().padStart(2, '0')
    return `${hours}:${mins}`
  }

  const mergeErrorDaysFromOverlaps = (overlaps: IOverlappingSchedulesResponse[]): IDriverSchedule[] => {
    const mergedSchedulesMap: { [key: string]: IDriverSchedule } = {}

    overlaps.map(overlap => {
      overlap.schedules.map(schedule => {
        const key = `${schedule.days.join(',')}-${schedule.from}-${schedule.until}`

        if (mergedSchedulesMap[key]) {
          const existingSchedule = mergedSchedulesMap[key]
          existingSchedule.errorDays = mergeArrays(existingSchedule.errorDays || [], schedule.errorDays || [])
        } else {
          mergedSchedulesMap[key] = { ...schedule }
        }
      })
    })

    return Object.values(mergedSchedulesMap)
  }

  const mergeArrays = (arr1: string[], arr2: string[]): string[] => {
    return Array.from(new Set([...arr1, ...arr2]))
  }

  const removeDuplicates = (array: string[]) => {
    return Array.from(new Set(array))
  }

  const checkOverlappingSchedules = (overlappingSchedules: IOverlappingSchedulesResponse[]) => {
    const currentWorkingHoursList = [...workingHoursList]
    let workingHoursWithError: IDriverSchedule[] = []

    currentWorkingHoursList.map(workingHour => {
      overlappingSchedules.map(overlappingSchedule => {
        overlappingSchedule.schedules.map(schedule => {
          if (
            schedule.days.toString() === workingHour.days.toString() &&
            schedule.from === workingHour.from &&
            schedule.until === workingHour.until
          ) {
            const filteredWorkingHoursWithError = workingHoursWithError.filter(filtered => {
              return (
                filtered.days.toString() !== workingHour.days.toString() &&
                filtered.from !== workingHour.from &&
                filtered.until !== workingHour.until
              )
            })

            const duplicateWorkingHoursWithError = workingHoursWithError.find(filtered => {
              return (
                filtered.days.toString() === workingHour.days.toString() &&
                filtered.from === workingHour.from &&
                filtered.until === workingHour.until
              )
            })

            const errorDays =
              duplicateWorkingHoursWithError === undefined || duplicateWorkingHoursWithError.errorDays === undefined
                ? overlappingSchedule.days
                : removeDuplicates([...duplicateWorkingHoursWithError.errorDays, ...overlappingSchedule.days])

            const workingHourItemWithError = {
              ...(duplicateWorkingHoursWithError !== undefined ? duplicateWorkingHoursWithError : workingHour),
              errorDays
            }

            workingHoursWithError = [...filteredWorkingHoursWithError, workingHourItemWithError]
          }
        })
      })
    })

    setWorkingHoursListErrors(workingHoursWithError)
  }

  const sortDays = (days: string[]): string[] => {
    const dayOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return days.slice().sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
  }

  const compareSchedules = (previous: IDriverSchedule[], updated: IDriverSchedule[]): string => {
    const formatSchedule = (schedule: IDriverSchedule) => {
      return `From ${schedule.from} to ${schedule.until} on ${sortDays(schedule.days).join(', ')}`
    }

    const messages: string[] = []

    const previousMap = new Map(
      previous.map(prev => [`${prev.from}-${prev.until}-${prev.days.sort().join(',')}`, prev])
    )
    const updatedMap = new Map(
      updated.map(updtd => [`${updtd.from}-${updtd.until}-${updtd.days.sort().join(',')}`, updtd])
    )

    // Check for removed schedules
    for (const [key, schedule] of Array.from(previousMap.entries())) {
      if (!updatedMap.has(key)) {
        messages.push(`Removed: ${formatSchedule(schedule)}`)
      }
    }

    // Check for added schedules
    for (const [key, schedule] of Array.from(updatedMap.entries())) {
      if (!previousMap.has(key)) {
        messages.push(`Added: ${formatSchedule(schedule)}`)
      }
    }

    // Check for modified schedules
    for (const prevSchedule of previous) {
      const updatedSchedule = updated.find(
        updtd => updtd.from === prevSchedule.from || updtd.until === prevSchedule.until
      )

      if (updatedSchedule) {
        const prevDays = new Set(prevSchedule.days)
        const updatedDays = new Set(updatedSchedule.days)

        if (prevSchedule.from !== updatedSchedule.from || prevSchedule.until !== updatedSchedule.until) {
          messages.push(
            `Time updated: ${prevSchedule.from}-${prevSchedule.until} → ${updatedSchedule.from}-${
              updatedSchedule.until
            }`
          )
        }

        const addedDays = Array.from(updatedDays).filter(day => !prevDays.has(day))
        const removedDays = Array.from(prevDays).filter(day => !updatedDays.has(day))

        if (addedDays.length > 0) {
          messages.push(
            `Days added: ${sortDays(addedDays).join(', ')} for time ${updatedSchedule.from}-${updatedSchedule.until}`
          )
        }

        if (removedDays.length > 0) {
          messages.push(
            `Days removed: ${sortDays(removedDays).join(', ')} for time ${updatedSchedule.from}-${
              updatedSchedule.until
            }`
          )
        }
      }
    }

    return messages.length > 0 ? messages.join('\n') : ''
  }

  const saveDriverSchedule = async () => {
    setIsSaving(true)

    if (checkForInvalidData()) {
      setIsSaving(false)
      notification.addNotification({
        title: 'Error',
        level: 'error',
        message: 'Cannot save an empty driver schedule item. Please delete it or add the necessary info.'
      })
    } else {
      const dto: IDriverScheduleDTO = {
        operatorId,
        serviceDays: workingHoursList
      }

      const response = await updateDriverSchedule(dto)

      setIsSaving(false)
      if (response !== null && response !== undefined && response.success === true) {
        const notificationMessage = compareSchedules(prevSchedule, workingHoursList)
        if (operatorId && notificationMessage) {
          sendPushNotificationToDriver(operatorId, notificationMessage)
        }
        history.push('/drivers/' + operatorId + '/schedule')
      } else if (
        response !== null &&
        response !== undefined &&
        response.success === false &&
        response.message === 'Conflicting schedules detected. Please review the overlapping scheduless.'
      ) {
        checkOverlappingSchedules(response.overlappingSchedules)
      } else if (response !== null && response !== undefined && response.success === false) {
        notification.addNotification({
          title: 'Error',
          level: 'error',
          message: 'Cannot save duplicate driver schedules. Please delete or modify the duplicate schedules.'
        })
      } else {
        notification.addNotification({
          title: 'Error',
          level: 'error',
          message: 'An error occurred while saving schedule. Please refresh page and try again.'
        })
      }
    }
  }

  return (
    <>
      <FloatingForm fullHeight={true}>
        <>
          <div className='form-modal-section full-section'>
            <div className='font-grey2 bold h5 mb1'>Flexible Availability Schedule</div>
            {loading ? (
              <div className='p3 m3 flex items-center justify-center h5 font-grey2'>
                <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
              </div>
            ) : null}
            {workingHoursList.length === 0 && loading !== true && (
              <div className='flex justify-center items-center my1 p3 font-grey border-light-grey4 rounded h5'>
                No Existing Driver Schedule
              </div>
            )}
            <DriverWorkingHours
              driverId={operatorId}
              workingHoursList={workingHoursList}
              setWorkingHoursList={setWorkingHoursList}
              workingHoursListErrors={workingHoursListErrors}
              setWorkingHoursListErrors={setWorkingHoursListErrors}
            />
          </div>
        </>
      </FloatingForm>
      <EditDriverBottomBar
        cancelPath={'/drivers/' + operatorId + '/schedule'}
        isSaving={isSaving}
        onSave={() => saveDriverSchedule()}
        disabled={workingHoursListErrors.length > 0}
      />
    </>
  )
}

export default DriverEditSchedule



// WEBPACK FOOTER //
// ./src/components/drivers/DriverEditSchedule.tsx